/**
 * Order and wait (BLU-1506): the pure logic behind the buyer's screens (the
 * To buy cards, Send, Arrived, the PO page, the PO list). No React, no tRPC,
 * so every rule here is pinned by a plain unit test.
 *
 *   statuses      one tone per row / PO status (StatusPill), the words live
 *                 with the components (they need `t`)
 *   supplier      which PO is still being built for a supplier
 *   floor time    "today", "has the send time passed" and "was an order to
 *                 this supplier sent today" in the FLOOR's timezone, never
 *                 the browser's
 *   Send          which lines go on this order and which stay out
 *   Arrived       the typed "what came" per blank, the `short` payload (only
 *                 the lines that came in short), and the live preview: the
 *                 same per-blank FIFO the server runs (oldest order first), so
 *                 the sentence under the form says what Save will do
 */
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { Locale } from 'date-fns/locale';
import type { TFunction } from 'i18next';
import type {
  OrderAndWaitOrderRow,
  OrderAndWaitPo,
  OrderAndWaitPoLine,
  OrderAndWaitRowStatus,
} from '@threadheads/shared';
import type { StatusPillTone } from '@/components/shared/StatusPill';
import { formatDayMonth } from '@/lib/format-dates';
import { normalizeNumericText } from '@/lib/parse-kg';

// ── Statuses ────────────────────────────────────────────────────────────────

/**
 * One tone per status (DESIGN.md section 1 and the PO status palette): out is
 * red (the supplier cannot fill it), short and late are amber (needs a look),
 * on the way is the PO palette's in transit cyan, arrived and used are emerald
 * (received), not sent yet and the quiet history states are grey.
 */
const ROW_STATUS_TONE: Record<OrderAndWaitRowStatus, StatusPillTone> = {
  out: 'error',
  short: 'warning',
  late: 'warning',
  not_sent: 'off',
  on_the_way: 'transit',
  arrived: 'on',
  used: 'on',
  dropped: 'off',
  spare: 'off',
};

const ROW_STATUSES = new Set<string>(Object.keys(ROW_STATUS_TONE));

export function isOrderAndWaitRowStatus(value: string | null | undefined): value is OrderAndWaitRowStatus {
  return typeof value === 'string' && ROW_STATUSES.has(value);
}

/**
 * The tone of a row, order or PO display status. A cancelled PO is red (the
 * cancelled / voided terminal state of DESIGN.md section 1); anything else
 * unknown is grey.
 */
export function orderAndWaitStatusTone(status: string | null | undefined): StatusPillTone {
  if (status === 'cancelled') return 'error';
  return isOrderAndWaitRowStatus(status) ? ROW_STATUS_TONE[status] : 'off';
}

/**
 * The PO page's action-bar status badge in each tone: the bar's Badge colours
 * (border 200, fill 50, words 700, as the other PO bars draw theirs), so an
 * Order and wait PO's bar reads like every other PO bar. Cyan is the PO
 * palette's in transit.
 */
export const ORDER_AND_WAIT_BAR_BADGE_TONE: Record<StatusPillTone, string> = {
  on: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  off: 'border-slate-200 bg-slate-50 text-slate-600',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  error: 'border-red-200 bg-red-50 text-red-700',
  transit: 'border-cyan-200 bg-cyan-50 text-cyan-700',
};

/** Arrived is a "received" action: the constructive outline-emerald of DESIGN.md section 3.4. */
export const ARRIVED_BUTTON_TONE = 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800';

/** The words for a status; the supplier name is the org's own, never hard-coded. */
export function orderAndWaitStatusLabel(
  t: TFunction<'orders'>,
  status: string | null | undefined,
  options: { supplierName?: string | null; dueDate?: string | null; locale: Locale },
): string {
  const due = options.dueDate ? formatDayMonth(options.dueDate, options.locale) : null;
  const supplier = options.supplierName?.trim() || t('oaw.yourSupplier');
  switch (status) {
    case 'out':
      return t('oaw.status.out', { supplier });
    case 'short':
      return t('oaw.status.short');
    case 'late':
      return due ? t('oaw.status.lateDue', { date: due }) : t('oaw.status.late');
    case 'not_sent':
      return t('oaw.status.notSent');
    case 'on_the_way':
      return due ? t('oaw.status.onTheWayDue', { date: due }) : t('oaw.status.onTheWay');
    case 'arrived':
      return t('oaw.status.arrived');
    case 'used':
      return t('oaw.status.used');
    case 'dropped':
      return t('oaw.status.dropped');
    case 'spare':
      return t('oaw.status.spare');
    case 'cancelled':
      return t('oaw.status.cancelled');
    default:
      return t('oaw.status.unknown');
  }
}

/**
 * The display status of an Order and wait PO from the PO list row alone (the
 * list read has no display status): draft = not sent, submitted = on the way
 * or late (the due day before the floor's today), received = arrived.
 */
export function orderAndWaitPoStatus(
  status: string,
  arrivalEtaDate: string | null | undefined,
  floorTodayIso: string | null,
): string {
  if (status === 'draft') return 'not_sent';
  if (status === 'received') return 'arrived';
  if (status === 'submitted') {
    return arrivalEtaDate && floorTodayIso && arrivalEtaDate.slice(0, 10) < floorTodayIso ? 'late' : 'on_the_way';
  }
  return status;
}

// ── Supplier ────────────────────────────────────────────────────────────────

type SupplierRef = Pick<OrderAndWaitPo, 'supplierId' | 'supplierName'>;

/** One key per supplier: its id, else its name (a PO with neither shares one key). */
export const supplierKey = (po: SupplierRef): string => po.supplierId ?? `name:${po.supplierName ?? ''}`;

/** The number of the supplier's PO still being built, where a short or out blank goes next; null when there is none. */
export function nextBuildingPoNumber(purchaseOrders: readonly OrderAndWaitPo[], po: SupplierRef): string | null {
  const key = supplierKey(po);
  return purchaseOrders.find((other) => other.status === 'draft' && supplierKey(other) === key)?.poNumber ?? null;
}

// ── Floor time ──────────────────────────────────────────────────────────────

const HH_MM = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;

/** "YYYY-MM-DD" today at the floor; the browser's own day when the zone is unknown or invalid. */
export function floorToday(timezone: string | null | undefined, now: Date = new Date()): string {
  if (timezone) {
    try {
      return formatInTimeZone(now, timezone, 'yyyy-MM-dd');
    } catch {
      // An invalid zone name: fall through to the browser's day.
    }
  }
  return format(now, 'yyyy-MM-dd');
}

function floorClock(timezone: string | null | undefined, now: Date): string {
  if (timezone) {
    try {
      return formatInTimeZone(now, timezone, 'HH:mm');
    } catch {
      // fall through
    }
  }
  return format(now, 'HH:mm');
}

const BARE_DAY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The floor-local day ("YYYY-MM-DD") of a stored moment; null when there is
 * none or it is not a date. A bare day is taken as is. A timestamp with no
 * zone is UTC: `purchase_orders.submitted_date` is a zoneless column stamped
 * with `now()` on a UTC database.
 */
export function floorDayOf(value: string | null | undefined, timezone: string | null | undefined): string | null {
  if (!value) return null;
  if (BARE_DAY.test(value)) return value;
  const iso = value.trim().replace(' ', 'T');
  const instant = new Date(HAS_ZONE.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(instant.getTime())) return null;
  return floorToday(timezone, instant);
}

/**
 * The suppliers (by `supplierKey`) with a purchase order sent today at the
 * floor, on the floor's clock. Today's daily send is done for them, so their
 * next order is due by the send time tomorrow. Reads the POs the To buy cards
 * already hold (building and sent); a box sent and marked arrived on the same
 * day is no longer among them.
 */
export function suppliersSentToday(
  purchaseOrders: readonly OrderAndWaitPo[],
  timezone: string | null | undefined,
  now: Date = new Date(),
): Set<string> {
  const today = floorToday(timezone, now);
  const keys = new Set<string>();
  for (const po of purchaseOrders) {
    if (po.status === 'draft') continue;
    if (floorDayOf(po.submittedDate, timezone) === today) keys.add(supplierKey(po));
  }
  return keys;
}

export interface SendByState {
  /** HH:MM, the floor-local daily send time. */
  time: string;
  /** The floor's clock is at or past the send time today, and nothing went to this supplier today. */
  passed: boolean;
  /** An order to this supplier was already sent today: the next one is due by the time tomorrow. */
  tomorrow: boolean;
}

/**
 * "Send by 2:00 pm" for a supplier that sends daily; null when the supplier
 * sends only when pressed, or the stored time is not a time. Display only:
 * nothing is sent automatically in this version.
 *
 * `sentToday` (from `suppliersSentToday`): today's order to this supplier is
 * already out, so the next one reads "Send by 2:00 pm tomorrow" and never
 * turns amber today.
 */
export function sendByState(
  sendMode: string | null | undefined,
  sendTime: string | null | undefined,
  timezone: string | null | undefined,
  now: Date = new Date(),
  sentToday = false,
): SendByState | null {
  if (sendMode !== 'daily' || !sendTime) return null;
  const time = sendTime.slice(0, 5);
  if (!HH_MM.test(time)) return null;
  if (sentToday) return { time, passed: false, tomorrow: true };
  return { time, passed: floorClock(timezone, now) >= time, tomorrow: false };
}

/** A floor-local HH:MM in the reader's words: "2:00 pm", or "14:00" in Chinese. */
export function formatSendTime(time: string, locale: Locale): string {
  const match = HH_MM.exec(time.slice(0, 5));
  if (!match) return time;
  const date = new Date(2000, 0, 1, Number(match[1]), Number(match[2]));
  return format(date, locale.code.startsWith('zh') ? 'HH:mm' : 'h:mm aaa', { locale });
}

// ── Lines ───────────────────────────────────────────────────────────────────

/** One line per blank: the server keys Send and Arrived by blank (sku id). */
export interface OrderAndWaitBlankLine {
  skuId: string;
  sku: string | null;
  title: string | null;
  colour: string | null;
  size: string | null;
  qty: number;
  waitingUnits: number;
  outUnits: number;
  shortUnits: number;
}

/**
 * The PO's lines folded to one per blank (a blank never has two lines on an
 * Order and wait PO today, but Arrived sums by blank, so this does too). A
 * line with no blank cannot be sent out or marked short and is left out.
 */
export function blankLines(lines: readonly OrderAndWaitPoLine[]): OrderAndWaitBlankLine[] {
  const bySku = new Map<string, OrderAndWaitBlankLine>();
  for (const line of lines) {
    if (!line.skuId) continue;
    const existing = bySku.get(line.skuId);
    if (existing) {
      existing.qty += line.qty;
      existing.waitingUnits += line.waitingUnits;
      existing.outUnits += line.outUnits;
      existing.shortUnits += line.shortUnits;
    } else {
      bySku.set(line.skuId, {
        skuId: line.skuId,
        sku: line.sku,
        title: line.title,
        colour: line.colour,
        size: line.size,
        qty: line.qty,
        waitingUnits: line.waitingUnits,
        outUnits: line.outUnits,
        shortUnits: line.shortUnits,
      });
    }
  }
  return [...bySku.values()];
}

export const sumQty = (lines: ReadonlyArray<{ qty: number }>): number =>
  lines.reduce((total, line) => total + line.qty, 0);

// ── Send ────────────────────────────────────────────────────────────────────

/**
 * The blanks that start the Send dialog marked "Out at {supplier}": the ones
 * that were out last time (their rows moved here flagged out). The buyer
 * turns one off when the supplier has it again.
 */
export function initialOutSkuIds(lines: readonly OrderAndWaitBlankLine[]): string[] {
  return lines.filter((line) => line.outUnits > 0).map((line) => line.skuId);
}

export interface SendPreview {
  sendLines: number;
  sendUnits: number;
  outLines: number;
  outUnits: number;
}

export function previewSend(lines: readonly OrderAndWaitBlankLine[], out: ReadonlySet<string>): SendPreview {
  const preview: SendPreview = { sendLines: 0, sendUnits: 0, outLines: 0, outUnits: 0 };
  for (const line of lines) {
    if (out.has(line.skuId)) {
      preview.outLines += 1;
      preview.outUnits += line.qty;
    } else if (line.qty > 0) {
      preview.sendLines += 1;
      preview.sendUnits += line.qty;
    }
  }
  return preview;
}

// ── Arrived ─────────────────────────────────────────────────────────────────

export type ArrivedParse = { ok: true; value: number } | { ok: false; reason: 'blank' | 'notWhole' | 'tooMany' };

/** What came for one blank: a whole number from 0 to what was ordered (full-width digits read as digits). */
export function parseArrived(text: string, ordered: number): ArrivedParse {
  const s = normalizeNumericText(text);
  if (s === '') return { ok: false, reason: 'blank' };
  if (!/^\d+$/.test(s)) return { ok: false, reason: 'notWhole' };
  const value = Number(s);
  if (value > ordered) return { ok: false, reason: 'tooMany' };
  return { ok: true, value };
}

export interface ArrivedPlan {
  /** Only the lines that came in short, as the server takes them. */
  short: Array<{ skuId: string; arrived: number }>;
  /** Blanks typed as arrived, per sku (every valid line, short or not). */
  arrivedBySku: Map<string, number>;
  orderedUnits: number;
  arrivedUnits: number;
  shortUnits: number;
  shortLines: number;
  invalidLines: number;
}

/**
 * The form's state as a request: a line nobody touched arrived in full; a
 * typed number below what was ordered is a short line. An invalid line blocks
 * Save (it is counted, never guessed).
 */
export function buildArrivedPlan(
  lines: readonly OrderAndWaitBlankLine[],
  texts: Readonly<Record<string, string>>,
): ArrivedPlan {
  const plan: ArrivedPlan = {
    short: [],
    arrivedBySku: new Map(),
    orderedUnits: 0,
    arrivedUnits: 0,
    shortUnits: 0,
    shortLines: 0,
    invalidLines: 0,
  };
  for (const line of lines) {
    plan.orderedUnits += line.qty;
    const text = texts[line.skuId];
    if (text === undefined) {
      plan.arrivedBySku.set(line.skuId, line.qty);
      plan.arrivedUnits += line.qty;
      continue;
    }
    const parsed = parseArrived(text, line.qty);
    if (!parsed.ok) {
      plan.invalidLines += 1;
      continue;
    }
    plan.arrivedBySku.set(line.skuId, parsed.value);
    plan.arrivedUnits += parsed.value;
    if (parsed.value < line.qty) {
      plan.short.push({ skuId: line.skuId, arrived: parsed.value });
      plan.shortUnits += line.qty - parsed.value;
      plan.shortLines += 1;
    }
  }
  return plan;
}

export interface ArrivalPreview {
  /** Orders whose every blank waiting on this PO is covered. */
  covered: number;
  /** Orders with at least one blank still short; they keep waiting. */
  short: number;
}

/**
 * What Arrived does to the orders, per blank, oldest order first (the server's
 * FIFO): a waiting row is covered while what came lasts; the first row it
 * cannot cover takes the rest and is short, and every row after it is short.
 * An order is covered when none of its waiting rows on this PO is short.
 * Rows that are not waiting (already arrived, used, spare) take no part.
 *
 * `orders` must be the PO's orders oldest first, as `list` scope `po` returns
 * them. An order that also waits on another PO is counted as covered here;
 * the toast after Save reports what really moved.
 */
export function previewArrival(
  orders: readonly OrderAndWaitOrderRow[],
  arrivedBySku: ReadonlyMap<string, number>,
  poId: string,
): ArrivalPreview {
  const left = new Map(arrivedBySku);
  const shortOrders = new Set<string>();
  const waitingOrders = new Set<string>();
  for (const order of orders) {
    for (const blank of order.blanks) {
      if (blank.state !== 'waiting' || blank.poId !== poId || !blank.skuId) continue;
      waitingOrders.add(order.prodOrderId);
      const available = left.get(blank.skuId) ?? 0;
      if (available >= blank.qty) {
        left.set(blank.skuId, available - blank.qty);
      } else {
        left.set(blank.skuId, 0);
        shortOrders.add(order.prodOrderId);
      }
    }
  }
  return { covered: waitingOrders.size - shortOrders.size, short: shortOrders.size };
}
